import { Registro } from '../types';
import { ReportTemplate, SEPROVINSA_LOGO } from '../types/reportConfig';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface PeriodComparison {
  currentPeriod: Registro[];
  previousPeriod: Registro[];
}

export const useEnhancedPDF = () => {
  // Generar insights automáticos
  const generateInsights = (registros: Registro[]): string[] => {
    const insights: string[] = [];

    // 1. Zona más crítica
    const lugarCounts = registros.reduce((acc, r) => {
      acc[r.lugar] = (acc[r.lugar] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topLugar = Object.entries(lugarCounts).sort((a, b) => b[1] - a[1])[0];
    if (topLugar) {
      const percentage = ((topLugar[1] / registros.length) * 100).toFixed(0);
      insights.push(
        `• Zona crítica: "${topLugar[0]}" con ${topLugar[1]} incidencias (${percentage}% del total)`
      );
    }

    // 2. Categoría principal
    const categoriaCounts = registros.reduce((acc, r) => {
      if (r.categoria) {
        acc[r.categoria] = (acc[r.categoria] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    const topCategoria = Object.entries(categoriaCounts).sort(
      (a, b) => b[1] - a[1]
    )[0];
    if (topCategoria) {
      insights.push(
        `• Categoría principal: "${topCategoria[0]}" con ${topCategoria[1]} incidencias`
      );
    }

    // 3. Horario crítico (si hay datos de hora)
    const horasCounts = registros.reduce((acc, r) => {
      const fecha = new Date(r.fecha);
      const hora = fecha.getHours();
      if (hora >= 6 && hora < 12) acc['Mañana (6-12)'] = (acc['Mañana (6-12)'] || 0) + 1;
      else if (hora >= 12 && hora < 18)
        acc['Tarde (12-18)'] = (acc['Tarde (12-18)'] || 0) + 1;
      else if (hora >= 18 && hora < 24)
        acc['Noche (18-24)'] = (acc['Noche (18-24)'] || 0) + 1;
      else acc['Madrugada (0-6)'] = (acc['Madrugada (0-6)'] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const topHorario = Object.entries(horasCounts).sort((a, b) => b[1] - a[1])[0];
    if (topHorario) {
      const percentage = ((topHorario[1] / registros.length) * 100).toFixed(0);
      insights.push(
        `• Horario crítico: ${topHorario[0]} con ${topHorario[1]} incidencias (${percentage}%)`
      );
    }

    return insights;
  };

  // Comparar periodos
  const comparePeriodsInsights = (
    current: Registro[],
    previous: Registro[]
  ): string[] => {
    const insights: string[] = [];

    // Comparar totales
    const diff = current.length - previous.length;
    const percentChange =
      previous.length > 0
        ? ((diff / previous.length) * 100).toFixed(0)
        : '0';

    if (diff > 0) {
      insights.push(
        `• Incremento de ${diff} incidencias (+${percentChange}%) vs periodo anterior`
      );
    } else if (diff < 0) {
      insights.push(
        `• Reducción de ${Math.abs(diff)} incidencias (${percentChange}%) vs periodo anterior`
      );
    } else {
      insights.push(`• Mismo número de incidencias que el periodo anterior`);
    }

    // Comparar categorías
    const currentCats = current.reduce((acc, r) => {
      if (r.categoria) acc[r.categoria] = (acc[r.categoria] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const previousCats = previous.reduce((acc, r) => {
      if (r.categoria) acc[r.categoria] = (acc[r.categoria] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Encontrar categoría con mayor mejora
    let maxImprovement = { cat: '', value: 0 };
    let maxDeterioration = { cat: '', value: 0 };

    Object.keys({ ...currentCats, ...previousCats }).forEach(cat => {
      const curr = currentCats[cat] || 0;
      const prev = previousCats[cat] || 0;
      const diff = curr - prev;

      if (diff < maxImprovement.value) {
        maxImprovement = { cat, value: diff };
      }
      if (diff > maxDeterioration.value) {
        maxDeterioration = { cat, value: diff };
      }
    });

    if (maxImprovement.value < 0) {
      const percent = previousCats[maxImprovement.cat]
        ? (
            (Math.abs(maxImprovement.value) / previousCats[maxImprovement.cat]) *
            100
          ).toFixed(0)
        : '0';
      insights.push(
        `• Mejora destacada: "${maxImprovement.cat}" ${percent}% menos incidencias`
      );
    }

    if (maxDeterioration.value > 0) {
      const percent = previousCats[maxDeterioration.cat]
        ? ((maxDeterioration.value / previousCats[maxDeterioration.cat]) * 100).toFixed(
            0
          )
        : '0';
      insights.push(
        `• Requiere atención: "${maxDeterioration.cat}" +${percent}% más incidencias`
      );
    }

    return insights;
  };

  // Generar PDF mejorado con template
  const generateEnhancedPDF = async (
    registros: Registro[],
    template: ReportTemplate,
    comparison?: PeriodComparison,
    filename = 'reporte.pdf'
  ) => {
    const doc = new jsPDF();
    let y = 10;

    // HEADER MODERNO - Línea de acento superior
    const rgb = hexToRgb(template.primaryColor);
    const rgbSecondary = hexToRgb(template.secondaryColor);
    
    // Línea de acento superior (gradiente simulado con dos colores)
    doc.setFillColor(rgb.r, rgb.g, rgb.b);
    doc.rect(10, y, 95, 2, 'F'); // Mitad izquierda
    doc.setFillColor(rgbSecondary.r, rgbSecondary.g, rgbSecondary.b);
    doc.rect(105, y, 95, 2, 'F'); // Mitad derecha

    y += 8;

    // LOGOS
    try {
      // Logo Seprovinsa (izquierda)
      const seproLogo = new window.Image();
      seproLogo.src = SEPROVINSA_LOGO;
      await new Promise(resolve => {
        seproLogo.onload = resolve;
        seproLogo.onerror = resolve;
      });
      
      if (seproLogo.complete) {
        const aspectRatio = seproLogo.width / seproLogo.height;
        const logoHeight = 14;
        const logoWidth = logoHeight * aspectRatio;
        doc.addImage(SEPROVINSA_LOGO, 'PNG', 14, y, logoWidth, logoHeight);
      }

      // Logo del Cliente (derecha)
      if (template.clientLogo) {
        const clientLogo = new window.Image();
        clientLogo.src = template.clientLogo;
        await new Promise(resolve => {
          clientLogo.onload = resolve;
          clientLogo.onerror = resolve;
        });
        
        if (clientLogo.complete) {
          const aspectRatio = clientLogo.width / clientLogo.height;
          const logoHeight = 14;
          const logoWidth = logoHeight * aspectRatio;
          doc.addImage(template.clientLogo, 'PNG', 196 - logoWidth, y, logoWidth, logoHeight);
        }
      }
    } catch (e) {
      console.error('Error al cargar logos:', e);
    }

    // Título (centrado, color secundario)
    doc.setFontSize(16);
    doc.setTextColor(rgbSecondary.r, rgbSecondary.g, rgbSecondary.b);
    doc.setFont('helvetica', 'bold');
    doc.text(template.headerText, 105, y + 8, { align: 'center' });

    // Fecha (centrado, gris)
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139); // #64748b
    doc.setFont('helvetica', 'normal');
    const fecha = new Date().toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    doc.text(fecha, 105, y + 13, { align: 'center' });

    // Línea inferior (color primario)
    y += 18;
    doc.setDrawColor(rgb.r, rgb.g, rgb.b);
    doc.setLineWidth(1);
    doc.line(10, y, 200, y);

    y += 8; // Espacio después del header

    // RESUMEN EJECUTIVO
    if (template.sections.summary) {
      doc.setFontSize(14);
      doc.setTextColor(rgb.r, rgb.g, rgb.b);
      doc.text('RESUMEN EJECUTIVO', 14, y + 8);

      doc.setFontSize(10);
      doc.setTextColor(33, 33, 33);
      doc.text(`Total de incidencias: ${registros.length}`, 14, y + 15);
      doc.text(
        `Periodo: ${new Date().toLocaleDateString('es-ES')}`,
        14,
        y + 21
      );

      // Categorías únicas
      const categorias = new Set(
        registros.map(r => r.categoria).filter(Boolean)
      ).size;
      doc.text(`Categorías reportadas: ${categorias}`, 14, y + 27);

      y += 35;
    }

    // CATEGORÍAS MÁS FRECUENTES
    if (template.sections.topCategories) {
      doc.setFontSize(14);
      doc.setTextColor(rgb.r, rgb.g, rgb.b);
      doc.text('CATEGORÍAS MÁS FRECUENTES', 14, y);

      const categoriaCounts = registros.reduce((acc, r) => {
        if (r.categoria) {
          acc[r.categoria] = (acc[r.categoria] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);

      // Ordenar por cantidad (descendente) - SIN LÍMITE
      const todasCategorias = Object.entries(categoriaCounts)
        .sort((a, b) => b[1] - a[1]);

      autoTable(doc, {
        startY: y + 5,
        head: [['Categoría', 'Cantidad', '% del Total']],
        body: todasCategorias.map(([cat, count]) => [
          cat,
          count.toString(),
          `${((count / registros.length) * 100).toFixed(1)}%`,
        ]),
        theme: 'grid',
        headStyles: {
          fillColor: [rgb.r, rgb.g, rgb.b],
          textColor: 255,
          fontStyle: 'bold',
        },
        margin: { left: 14, right: 14 },
      });

      y = (doc as any).lastAutoTable.finalY + 10;
    }

    // INSIGHTS AUTOMÁTICOS
    if (template.sections.insights) {
      doc.setFontSize(14);
      doc.setTextColor(rgb.r, rgb.g, rgb.b);
      doc.text('HALLAZGOS CLAVE', 14, y);

      doc.setFontSize(10);
      doc.setTextColor(33, 33, 33);

      const insights = generateInsights(registros);
      insights.forEach((insight, i) => {
        doc.text(insight, 14, y + 8 + i * 6);
      });

      y += 8 + insights.length * 6 + 5;

      // Comparativas si están disponibles
      if (comparison) {
        doc.setFontSize(12);
        doc.setTextColor(rgb.r, rgb.g, rgb.b);
        doc.text('COMPARATIVA CON PERIODO ANTERIOR', 14, y);

        doc.setFontSize(10);
        doc.setTextColor(33, 33, 33);

        const compInsights = comparePeriodsInsights(
          comparison.currentPeriod,
          comparison.previousPeriod
        );
        compInsights.forEach((insight, i) => {
          doc.text(insight, 14, y + 8 + i * 6);
        });

        y += 8 + compInsights.length * 6 + 5;
      }
    }

    // LISTADO DETALLADO
    if (template.sections.detailedList) {
      // Nueva página si es necesario
      if (y > 200) {
        doc.addPage();
        y = 15;
      }

      doc.setFontSize(14);
      doc.setTextColor(rgb.r, rgb.g, rgb.b);
      doc.text('LISTADO DETALLADO', 14, y);

      const tableData = registros.slice(0, 50).map(r => [
        new Date(r.fecha).toLocaleDateString('es-ES'),
        r.lugar,
        r.incidencia || '',
        r.categoria || 'Sin categoría',
      ]);

      autoTable(doc, {
        startY: y + 5,
        head: [['Fecha', 'Lugar', 'Incidencia', 'Categoría']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [rgb.r, rgb.g, rgb.b],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center',
        },
        alternateRowStyles: { fillColor: [240, 248, 255] },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 40 },
          2: { cellWidth: 70 },
          3: { cellWidth: 35 },
        },
        margin: { left: 14, right: 14 },
      });
    }

    // FOOTER en todas las páginas
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageHeight = doc.internal.pageSize.height;
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(template.footerText, 14, pageHeight - 8);
      doc.text(
        `Generado: ${new Date().toLocaleString('es-ES')}`,
        14,
        pageHeight - 3
      );
      doc.text(`Página ${i} de ${pageCount}`, 196, pageHeight - 3, {
        align: 'right',
      });
    }

    doc.save(filename);
  };

  return {
    generateEnhancedPDF,
    generateInsights,
    comparePeriodsInsights,
  };
};

// Utilidad para convertir hex a RGB
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 136, b: 254 };
}
